import { useMemo, useState } from "react";
import {
  Plane,
  Clock,
  ArrowRight,
  Filter,
  X,
  Sparkles,
  TrendingDown,
  Zap,
  Info,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import type { FlightOffer } from "@/lib/travelpayouts.server";
import { saveSelectedFlight } from "@/lib/selected-flight";

type SortKey = "best" | "cheapest" | "fastest" | "departure";

const AIRLINE_NAMES: Record<string, string> = {
  AM: "Aeroméxico",
  Y4: "Volaris",
  VB: "VivaAerobus",
  UA: "United",
  AA: "American Airlines",
  DL: "Delta",
  IB: "Iberia",
  AV: "Avianca",
  CM: "Copa Airlines",
  LA: "LATAM",
  AC: "Air Canada",
  AF: "Air France",
  KL: "KLM",
  BA: "British Airways",
  LH: "Lufthansa",
  TK: "Turkish Airlines",
  EK: "Emirates",
  QR: "Qatar Airways",
  B6: "JetBlue",
  WN: "Southwest",
  AS: "Alaska Airlines",
};

function airlineName(iata: string): string {
  return AIRLINE_NAMES[iata] ?? iata;
}
function airlineLogo(iata: string): string {
  return `https://pics.avs.io/120/60/${iata}.png`;
}
function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
}
function durationLabel(min?: number): string {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

/** Solo datos reales de Travelpayouts: sin hora o duración, no se muestran. */
function realSchedule(o: FlightOffer): { depTime: string | null; arrTime: string | null; durationMin: number | null } {
  const fmt = (n: number) => `${Math.floor(n / 60) % 24}`.padStart(2, "0") + ":" + `${n % 60}`.padStart(2, "0");
  const m = o.departAt ? /T(\d{2}):(\d{2})/.exec(o.departAt) : null;
  const dep = m ? Number(m[1]) * 60 + Number(m[2]) : null;
  const durationMin = o.durationToMinutes ?? o.durationMinutes ?? null;
  return {
    depTime: dep != null ? fmt(dep) : null,
    arrTime: dep != null && durationMin ? fmt(dep + durationMin) : null,
    durationMin: durationMin || null,
  };
}
const durOf = (s: { durationMin: number | null }) => s.durationMin ?? Number.POSITIVE_INFINITY;

export function FlightResults({
  offers,
  loading,
  error,
}: {
  offers: FlightOffer[];
  loading?: boolean;
  error?: string | null;
}) {
  const [sort, setSort] = useState<SortKey>("best");
  const [pageSize, setPageSize] = useState(15);
  const [maxStops, setMaxStops] = useState<number | "any">("any");
  const [airlines, setAirlines] = useState<Set<string>>(new Set());
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [mobileFilters, setMobileFilters] = useState(false);

  const allAirlines = useMemo(
    () => Array.from(new Set(offers.map((o) => o.airline))).sort(),
    [offers],
  );

  const priceBounds = useMemo(() => {
    if (offers.length === 0) return { min: 0, max: 0 };
    const prices = offers.map((o) => o.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [offers]);

  // Stats for the "Best / Cheapest / Fastest" tabs
  const stats = useMemo(() => {
    if (offers.length === 0) return null;
    const sched = new Map(offers.map((o) => [o, realSchedule(o)] as const));
    const sortedPrice = [...offers].sort((a, b) => a.price - b.price);
    const sortedDur = [...offers].sort(
      (a, b) => durOf(sched.get(a)!) - durOf(sched.get(b)!),
    );
    const cheapest = sortedPrice[0];
    const fastest = sortedDur[0];
    const priceMin = sortedPrice[0].price;
    const durMin = durOf(sched.get(fastest)!);
    const best = [...offers].sort((a, b) => {
      const score = (o: FlightOffer) =>
        (o.price / priceMin) * 0.6 +
        (Number.isFinite(durMin) && sched.get(o)!.durationMin ? sched.get(o)!.durationMin! / durMin : 1) * 0.4 +
        o.transfers * 0.05;
      return score(a) - score(b);
    })[0];
    return {
      best,
      cheapest,
      fastest,
      bestPrice: best.price,
      cheapestPrice: cheapest.price,
      fastestPrice: fastest.price,
      fastestDuration: sched.get(fastest)!.durationMin ?? undefined,
      bestDuration: sched.get(best)!.durationMin ?? undefined,
      cheapestDuration: sched.get(cheapest)!.durationMin ?? undefined,
    };
  }, [offers]);

  const filtered = useMemo(() => {
    const list = offers.filter((o) => {
      if (maxStops !== "any" && o.transfers > maxStops) return false;
      if (airlines.size > 0 && !airlines.has(o.airline)) return false;
      if (maxPrice !== null && o.price > maxPrice) return false;
      return true;
    });
    const sched = new Map(list.map((o) => [o, realSchedule(o)] as const));
    const priceMin = Math.min(...list.map((o) => o.price), Infinity);
    const durMin = Math.min(...list.map((o) => durOf(sched.get(o)!)), Infinity);
    return list.sort((a, b) => {
      if (sort === "cheapest") return a.price - b.price;
      if (sort === "fastest") return durOf(sched.get(a)!) - durOf(sched.get(b)!);
      if (sort === "departure") return (sched.get(a)!.depTime ?? "99").localeCompare(sched.get(b)!.depTime ?? "99");
      const score = (o: FlightOffer) => {
        const p = priceMin > 0 ? o.price / priceMin : 1;
        const d = Number.isFinite(durMin) && sched.get(o)!.durationMin ? sched.get(o)!.durationMin! / durMin : 1;
        return p * 0.6 + d * 0.4 + o.transfers * 0.05;
      };
      return score(a) - score(b);
    });
  }, [offers, sort, maxStops, airlines, maxPrice]);

  const visible = useMemo(() => filtered.slice(0, pageSize), [filtered, pageSize]);

  if (loading) return <ResultsSkeleton />;

  if (error) {
    return (
      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        Sin resultados para esta ruta. Prueba otras fechas o destinos.
      </div>
    );
  }

  const Filters = (
    <Sidebar
      allAirlines={allAirlines}
      airlines={airlines}
      setAirlines={setAirlines}
      maxStops={maxStops}
      setMaxStops={setMaxStops}
      maxPrice={maxPrice}
      setMaxPrice={setMaxPrice}
      bounds={priceBounds}
    />
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <aside className="hidden lg:block">
        <div className="sticky top-24">{Filters}</div>
      </aside>

      <div>
        {/* Skyscanner-style Best/Cheapest/Fastest tab strip */}
        {stats && (
          <div className="mb-4 grid grid-cols-3 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <SortTab
              active={sort === "best"}
              onClick={() => setSort("best")}
              icon={Sparkles}
              label="Mejor"
              price={stats.bestPrice}
              meta={stats.bestDuration ? durationLabel(stats.bestDuration) : ""}
            />
            <SortTab
              active={sort === "cheapest"}
              onClick={() => setSort("cheapest")}
              icon={TrendingDown}
              label="Más barato"
              price={stats.cheapestPrice}
              meta={stats.cheapestDuration ? durationLabel(stats.cheapestDuration) : ""}
            />
            <SortTab
              active={sort === "fastest"}
              onClick={() => setSort("fastest")}
              icon={Zap}
              label="Más rápido"
              price={stats.fastestPrice}
              meta={stats.fastestDuration ? durationLabel(stats.fastestDuration) : ""}
            />
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
            {filtered.length === 1 ? "resultado" : "resultados"}
            {filtered.length !== offers.length && ` de ${offers.length}`}
            <span className="ml-2 hidden text-xs text-muted-foreground/80 sm:inline">
              · Precios por persona, impuestos incluidos
            </span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileFilters(true)}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground lg:hidden"
            >
              <Filter className="h-3.5 w-3.5" /> Filtros
            </button>
          </div>
        </div>

        <ul className="space-y-3">
          {visible.map((o, i) => {
            const isCheapest = stats && o === stats.cheapest;
            const isFastest = stats && o === stats.fastest;
            const isBest = stats && o === stats.best;
            return (
              <li key={`${o.airline}-${o.destination}-${o.departAt ?? o.departDate}-${i}`}>
                <FlightCard
                  offer={o}
                  badges={{
                    best: !!isBest,
                    cheapest: !!isCheapest,
                    fastest: !!isFastest,
                  }}
                />
              </li>
            );
          })}
        </ul>

        {filtered.length > visible.length && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setPageSize((n) => n + 15)}
              className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-primary hover:text-primary"
            >
              Mostrar más vuelos ({filtered.length - visible.length} restantes)
            </button>
          </div>
        )}
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-display text-lg font-semibold">Filtros</h3>
            <button
              type="button"
              onClick={() => setMobileFilters(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">{Filters}</div>
          <div className="border-t border-border p-4">
            <button
              type="button"
              onClick={() => setMobileFilters(false)}
              className="h-12 w-full rounded-xl bg-primary font-semibold text-primary-foreground"
            >
              Ver {filtered.length} resultados
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SortTab({
  active,
  onClick,
  icon: Icon,
  label,
  price,
  meta,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Sparkles;
  label: string;
  price: number;
  meta: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col items-start gap-1 border-b-2 px-3 py-3 text-left transition sm:px-5 sm:py-4 ${
        active
          ? "border-primary bg-primary/5"
          : "border-transparent hover:bg-muted/40"
      }`}
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-xs">
        <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : ""}`} />
        {label}
      </span>
      <span className="font-display text-base font-semibold leading-none text-foreground sm:text-lg">
        ${price.toLocaleString("es-MX")}
      </span>
      <span className="text-[10px] text-muted-foreground sm:text-xs">{meta}</span>
    </button>
  );
}

function Sidebar({
  allAirlines,
  airlines,
  setAirlines,
  maxStops,
  setMaxStops,
  maxPrice,
  setMaxPrice,
  bounds,
}: {
  allAirlines: string[];
  airlines: Set<string>;
  setAirlines: (s: Set<string>) => void;
  maxStops: number | "any";
  setMaxStops: (v: number | "any") => void;
  maxPrice: number | null;
  setMaxPrice: (v: number | null) => void;
  bounds: { min: number; max: number };
}) {
  const hasFilters =
    airlines.size > 0 || maxStops !== "any" || maxPrice !== null;

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold">Filtros</h3>
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setAirlines(new Set());
              setMaxStops("any");
              setMaxPrice(null);
            }}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      <FilterBlock title="Escalas">
        <div className="space-y-2">
          {(
            [
              ["any", "Cualquiera"],
              [0, "Directo"],
              [1, "1 escala"],
              [2, "2+ escalas"],
            ] as const
          ).map(([val, label]) => (
            <label key={String(val)} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="stops"
                checked={maxStops === val}
                onChange={() => setMaxStops(val as number | "any")}
                className="h-4 w-4 accent-primary"
              />
              {label}
            </label>
          ))}
        </div>
      </FilterBlock>

      <FilterBlock title="Precio máximo">
        <input
          type="range"
          min={bounds.min}
          max={bounds.max || bounds.min + 1}
          step={Math.max(50, Math.round((bounds.max - bounds.min) / 100))}
          value={maxPrice ?? bounds.max}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full accent-primary"
          disabled={bounds.max === bounds.min}
        />
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>${bounds.min.toLocaleString("es-MX")}</span>
          <span className="font-semibold text-foreground">
            hasta ${(maxPrice ?? bounds.max).toLocaleString("es-MX")}
          </span>
        </div>
      </FilterBlock>

      <FilterBlock title="Aerolíneas">
        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {allAirlines.map((a) => (
            <label key={a} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={airlines.has(a)}
                onChange={() => {
                  const next = new Set(airlines);
                  if (next.has(a)) next.delete(a);
                  else next.add(a);
                  setAirlines(next);
                }}
                className="h-4 w-4 accent-primary"
              />
              <img
                src={airlineLogo(a)}
                alt=""
                width={24}
                height={12}
                loading="lazy"
                className="h-3 w-auto"
              />
              {airlineName(a)}
            </label>
          ))}
        </div>
      </FilterBlock>
    </div>
  );
}

function FilterBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </h4>
      {children}
    </div>
  );
}

function FlightCard({
  offer,
  badges,
}: {
  offer: FlightOffer;
  badges: { best: boolean; cheapest: boolean; fastest: boolean };
}) {
  const navigate = useNavigate();
  const sched = realSchedule(offer);
  const stopsLabel =
    offer.transfers === 0
      ? "Directo"
      : `${offer.transfers} escala${offer.transfers > 1 ? "s" : ""}`;

  function viewDetails() {
    saveSelectedFlight(offer);
    navigate({ to: "/flights/details" });
  }
  function bookNow() {
    saveSelectedFlight(offer);
    if (typeof window !== "undefined" && offer.deeplink) {
      window.open(offer.deeplink, "_blank", "noopener,noreferrer");
    }
  }

  const showBadgeRow = badges.best || badges.cheapest || badges.fastest;

  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevated"
    >
      {showBadgeRow && (
        <div className="flex flex-wrap gap-1.5 border-b border-border/60 bg-muted/30 px-4 py-2 sm:px-5">
          {badges.best && (
            <Badge tone="primary" icon={Sparkles}>
              Mejor opción
            </Badge>
          )}
          {badges.cheapest && (
            <Badge tone="success" icon={TrendingDown}>
              Precio más bajo
            </Badge>
          )}
          {badges.fastest && (
            <Badge tone="info" icon={Zap}>
              Más rápido
            </Badge>
          )}
        </div>
      )}

      <div
        onClick={viewDetails}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            viewDetails();
          }
        }}
        className="flex cursor-pointer flex-col gap-4 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-5 md:flex-row md:items-center"
      >
        {/* Airline */}
        <div className="flex items-center gap-3 md:w-40">
          <img
            src={airlineLogo(offer.airline)}
            alt={airlineName(offer.airline)}
            width={48}
            height={24}
            loading="lazy"
            className="h-9 w-9 shrink-0 rounded-lg bg-white object-contain p-1 ring-1 ring-border"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">
              {airlineName(offer.airline)}
            </div>
            {offer.flightNumber && (
              <div className="text-[11px] text-muted-foreground">
                Vuelo {offer.airline} {offer.flightNumber}
              </div>
            )}
          </div>
        </div>

        {/* Times + route bar */}
        <div className="flex flex-1 items-center gap-3 sm:gap-5">
          <div className="text-center">
            <div className="font-display text-xl font-semibold leading-none sm:text-2xl">
              {sched.depTime ?? ""}
            </div>
            <div className="mt-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground">
              {offer.origin}
            </div>
          </div>

          <div className="flex flex-1 flex-col items-center">
            <span className="mb-1 text-[11px] font-medium text-muted-foreground">
              {sched.durationMin ? durationLabel(sched.durationMin) : ""}
            </span>
            <div className="relative h-[2px] w-full rounded-full bg-border">
              {offer.transfers > 0 &&
                Array.from({ length: offer.transfers }).map((_, idx) => (
                  <span
                    key={idx}
                    className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-card bg-muted-foreground"
                    style={{
                      left: `${((idx + 1) / (offer.transfers + 1)) * 100}%`,
                      marginLeft: -4,
                    }}
                  />
                ))}
              <Plane className="absolute -top-[7px] right-0 h-3.5 w-3.5 rotate-90 fill-primary text-primary" />
            </div>
            <span
              className={`mt-1 text-[10px] font-semibold uppercase tracking-wider ${
                offer.transfers === 0 ? "text-success" : "text-muted-foreground"
              }`}
            >
              {stopsLabel}
            </span>
          </div>

          <div className="text-center">
            <div className="font-display text-xl font-semibold leading-none sm:text-2xl">
              {sched.arrTime ?? ""}
            </div>
            <div className="mt-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground">
              {offer.destination}
            </div>
          </div>
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between gap-3 border-t border-border pt-4 md:w-44 md:flex-col md:items-end md:border-l md:border-t-0 md:pl-5 md:pt-0">
          <div className="md:text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Precio referencial por persona
            </div>
            <div className="font-display text-2xl font-semibold leading-none text-foreground">
              ${offer.price.toLocaleString("es-MX")}
              <span className="ml-1 text-xs font-medium text-muted-foreground">
                {offer.currency}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground md:justify-end">
              <Clock className="h-3 w-3" /> {formatDate(offer.departDate)}
              {offer.returnDate && <> · {formatDate(offer.returnDate)}</>}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              bookNow();
            }}
            className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-glow transition-transform group-hover:scale-[1.03]"
          >
            Ver oferta <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Footer meta strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/60 bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground sm:px-5">
        <span className="inline-flex items-center gap-1">
          <Info className="h-3 w-3" /> Precio referencial: se confirma con la aerolínea o agencia socia al reservar.
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            viewDetails();
          }}
          className="ml-auto inline-flex items-center gap-1 font-semibold text-primary hover:underline"
        >
          <Info className="h-3 w-3" /> Ver detalles
        </button>
      </div>
    </article>
  );
}

function Badge({
  tone,
  icon: Icon,
  children,
}: {
  tone: "primary" | "success" | "info";
  icon: typeof Sparkles;
  children: React.ReactNode;
}) {
  const cls =
    tone === "success"
      ? "bg-success/10 text-success ring-success/20"
      : tone === "info"
        ? "bg-primary/10 text-primary ring-primary/20"
        : "bg-gradient-to-r from-primary/15 to-primary/5 text-primary ring-primary/20";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${cls}`}
    >
      <Icon className="h-3 w-3" /> {children}
    </span>
  );
}

function ResultsSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <div className="hidden h-80 animate-pulse rounded-2xl bg-muted lg:block" />
      <div className="space-y-3">
        <div className="h-20 animate-pulse rounded-2xl bg-muted" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-2xl bg-muted" />
        ))}
      </div>
    </div>
  );
}
